<!-- eslint-disable vue/html-closing-bracket-newline -->
<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- eslint-disable vue/max-attributes-per-line -->
<!-- eslint-disable vue/html-indent -->
<template>
    <div class="bg-[#E5E5F2] h-full  p-4">
        <div style="max-width: 1600px" class="mx-auto p-6 bg-white rounded-[8px] h-full">
            <div v-if="pendingFieldAttachmentUuids.length" class="top-1.5 sticky h-0 z-20 max-w-2xl mx-auto">
                <div class="alert border-base-content/30 py-2 px-2.5">
                    <IconInfoCircle class="stroke-info shrink-0 w-6 h-6" />
                    <span>{{ t('uploaded_pdf_contains_form_fields_keep_or_remove_them') }}</span>
                    <div>
                        <button class="btn btn-sm" @click.prevent="removePendingFields">
                            {{ t('remove') }}
                        </button>
                        <button class="btn btn-sm btn-neutral text-white" @click.prevent="save">
                            {{ t('keep') }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="$slots.buttons || withTitle" id="title_container"
                class="flex flex-col md:flex-row md:justify-between md:items-start gap-2 md:gap-3 top-0 mb-4 z-30 bg-white py-1.5"
                :class="{ sticky: withStickySubmitters || isBreakpointLg }">
                <div class="flex items-start gap-2 lg:gap-3 min-w-0 w-full md:flex-1">
                    <a v-if="withLogo" href="/" class="shrink-0">
                        <Logo />
                    </a>
                    <Contenteditable v-if="withTitle" :model-value="template.name" :editable="editable"
                        class="text-base md:text-xl lg:text-2xl font-semibold focus:text-clip min-w-0"
                        :icon-stroke-width="2.3" @update:model-value="updateName" />
                </div>
                <div class="flex flex-row flex-wrap gap-2 lg:gap-3 items-center justify-between md:shrink-0">
                    <slot v-if="$slots.buttons" name="buttons" />
                    <template v-else>
                        <div v-if="editable" class="flex items-center gap-1.5 sm:gap-2">
                            <button id="save-button"
                                class="button-cs-primary-sm lg:button-cs-primary flex items-center gap-1.5"
                                :class="{ disabled: isSaving }" v-bind="isSaving ? { disabled: true } : {}"
                                @click.prevent="onSaveDraftClick">
                                <IconInnerShadowTop v-if="isSaving" width="18" class="animate-spin shrink-0" />
                                <IconDeviceFloppy v-else width="18" class="shrink-0" />
                                <span class="text-xs md:text-sm font-medium">Save Draft</span>
                            </button>
                            <button
                                class="button-cs-outline-primary-sm lg:button-cs-outline-primary flex items-center gap-1.5"
                                :class="{ disabled: isSaving }" v-bind="isSaving ? { disabled: true } : {}"
                                @click.prevent="onPreviewClick">
                                <IconEye class="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                                <span class="text-xs md:text-sm font-medium">Preview</span>
                            </button>
                        </div>
                        <a v-if="editable" :href="`/templates/${template.id}/submissions/new?with_link=true`"
                            data-turbo-frame="modal"
                            class="button-cs-outline-primary-sm lg:button-cs-outline-primary flex items-center gap-1.5"
                            @click="maybeShowErrorTemplateAlert">
                            <IconSend class="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                            <span class="text-xs md:text-sm font-medium">
                                {{ t('send') }}
                            </span>
                        </a>
                        <a v-else :href="`/templates/${template.id}`" class="button-cs-primary">
                            <span class="hidden md:inline">
                                {{ t('back') }}
                            </span>
                        </a>
                    </template>
                </div>
            </div>
            <div id="main_container" class="flex"
                :class="$slots.buttons || withTitle ? 'md:max-h-[calc(100%_-_60px)]' : 'md:max-h-[100%]'">
                <div v-if="withDocumentsList" id="documents_container" ref="previews"
                    :style="{ 'display': isBreakpointLg ? 'none' : 'initial' }"
                    class="overflow-y-auto overflow-x-hidden w-52 flex-none pr-3 mt-0.5 pt-0.5 hidden lg:block">
                    <div v-for="(item, index) in template.schema" :key="index"
                        class="mb-4 border rounded-md overflow-hidden">
                        <!-- Accordion header -->
                        <div class="flex justify-between items-center p-2 bg-gray-100 cursor-pointer"
                            @click="toggleAccordion(index)">
                            <div class="font-medium text-sm truncate flex-grow">
                                {{ item.name || `Document ${index + 1}` }}
                            </div>
                            <button class="text-gray-500 focus:outline-none">
                                <svg class="w-4 h-4 transition-transform"
                                    :class="{ 'rotate-180': !isAccordionClosed(index) }" fill="none"
                                    stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        <!-- Accordion content -->
                        <div v-show="!isAccordionClosed(index)" class="p-2">
                            <DocumentPreview :with-arrows="template.schema.length > 1" :item="item"
                                :document="sortedDocuments[index]" :accept-file-types="acceptFileTypes"
                                :with-replace-button="withUploadButton" :editable="editable" :template="template"
                                :document-id="item.id" :document-index="index" :document-uuid="item.attachment_uuid"
                                :all-fields="template.fields"
                                @scroll-to="(item, pageIndex) => scrollIntoDocument(item, pageIndex)"
                                @remove="onDocumentRemove" @replace="onDocumentReplace" @up="moveDocument(item, -1)"
                                @down="moveDocument(item, 1)" @change="save" />
                        </div>
                    </div>
                    <div class="sticky bottom-0 py-2">
                        <Upload v-if="sortedDocuments.length && editable && withUploadButton"
                            :accept-file-types="acceptFileTypes" :template-id="template.id"
                            @success="updateFromUpload" />
                    </div>
                </div>
                <div id="pages_container"
                    class="w-full overflow-y-hidden md:overflow-y-auto overflow-x-hidden mt-0.5 pt-0.5">
                    <div ref="documents" class="pr-3.5 pl-0.5">
                        <Dropzone v-if="!sortedDocuments.length && withUploadButton" :template-id="template.id"
                            :accept-file-types="acceptFileTypes" @success="updateFromUpload" />
                        <template v-else>
                            <template v-for="document in sortedDocuments" :key="document.uuid">
                                <Document :ref="setDocumentRefs" :areas-index="fieldAreasIndex[document.uuid]"
                                    :selected-submitter="selectedSubmitter" :document="document" :is-drag="!!dragField"
                                    :default-fields="[...defaultRequiredFields, ...defaultFields]"
                                    :allow-draw="!onlyDefinedFields" :default-submitters="defaultSubmitters"
                                    :draw-field="drawField" :draw-field-type="drawFieldType" :editable="editable"
                                    :base-url="baseUrl"
                                    @draw="[onDraw($event), withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]"
                                    @drop-field="onDropfield" @remove-area="removeArea" />
                                <DocumentControls v-if="isBreakpointLg && editable"
                                    :with-arrows="template.schema.length > 1"
                                    :item="template.schema.find((item) => item.attachment_uuid === document.uuid)"
                                    :with-replace-button="withUploadButton" :accept-file-types="acceptFileTypes"
                                    :document="document" :template="template"
                                    class="pb-2 mb-2 border-b border-base-300 border-dashed" @remove="onDocumentRemove"
                                    @replace="onDocumentReplace"
                                    @up="moveDocument(template.schema.find((item) => item.attachment_uuid === document.uuid), -1)"
                                    @down="moveDocument(template.schema.find((item) => item.attachment_uuid === document.uuid), 1)"
                                    @change="save" />
                            </template>
                            <div v-if="sortedDocuments.length && isBreakpointLg && editable" class="pb-4">
                                <Upload v-if="withUploadButton" :template-id="template.id"
                                    :accept-file-types="acceptFileTypes" @success="updateFromUpload" />
                            </div>
                        </template>
                    </div>
                </div>
                <div v-if="withFieldsList" id="fields_list_container"
                    class="relative w-80 flex-none mt-1  pl-0.5 hidden md:block"
                    :class="drawField ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'">
                    <div v-if="showDrawField || drawField" class="sticky bg-cs-gray-lv-0.5 inset-0 h-full z-20">
                        <div class="bg-white rounded-lg p-5 text-center space-y-4">
                            <!-- Make if approval can't draw on builder template -->
                            <template v-if="drawField?.type === 'approval' || drawFieldType === 'approval'">
                                <div>
                                    <button class="button-cs-primary" @click="clearDrawField">
                                        {{ t('cancel') }}
                                    </button>
                                    <a v-if="!drawField && !drawOption && !['stamp', 'signature', 'initials'].includes(drawField?.type || drawFieldType)"
                                        href="#" class="link block mt-3 text-sm"
                                        @click.prevent="[addField(drawFieldType), drawField = null, drawOption = null, withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]">
                                        {{ t('add_field_without_drawing') }}
                                    </a>
                                </div>
                            </template>
                            <template v-else>
                                <p>
                                    {{ t('draw_field_on_the_document').replace('{field}', drawField?.name || '') }}
                                </p>
                                <div>
                                    <button class="button-cs-primary" @click="clearDrawField">
                                        {{ t('cancel') }}
                                    </button>
                                    <a v-if="!drawField && !drawOption && !['stamp', 'signature', 'initials'].includes(drawField?.type || drawFieldType)"
                                        href="#" class="link block mt-3 text-sm"
                                        @click.prevent="[addField(drawFieldType), drawField = null, drawOption = null, withSelectedFieldType ? '' : drawFieldType = '', showDrawField = false]">
                                        {{ t('or_add_field_without_drawing') }}
                                    </a>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div>
                        <Fields ref="fields" :fields="template.fields" :submitters="template.submitters"
                            :selected-submitter="selectedSubmitter" :with-help="withHelp"
                            :default-submitters="defaultSubmitters" :draw-field-type="drawFieldType"
                            :default-fields="[...defaultRequiredFields, ...defaultFields]"
                            :default-required-fields="defaultRequiredFields" :field-types="fieldTypes"
                            :with-sticky-submitters="false" :only-defined-fields="onlyDefinedFields"
                            :editable="editable" @add-field="addField"
                            @set-draw="[drawField = $event.field, drawOption = $event.option]"
                            @set-draw-type="[drawFieldType = $event, showDrawField = true]"
                            @set-drag="dragField = $event" @change-submitter="selectedSubmitter = $event"
                            @drag-end="dragField = null" @scroll-to-area="scrollToArea" />
                    </div>
                </div>
            </div>
            <div class="sticky bottom-0">
                <MobileDrawField v-if="drawField && isBreakpointLg" :draw-field="drawField" :fields="template.fields"
                    :submitters="template.submitters" :selected-submitter="selectedSubmitter" class="md:hidden"
                    :editable="editable" @cancel="[drawField = null, drawOption = null]"
                    @change-submitter="[selectedSubmitter = $event, drawField.submitter_uuid = $event.uuid]" />
                <MobileFields v-if="sortedDocuments.length && !drawField && editable" :fields="template.fields"
                    :default-fields="[...defaultRequiredFields, ...defaultFields]"
                    :default-required-fields="defaultRequiredFields" :field-types="fieldTypes"
                    :selected-submitter="selectedSubmitter" @select="startFieldDraw($event)" />
            </div>
            <div id="docuseal_modal_container" />

            <!-- Save success toast -->
            <div v-if="showSaveToast" class="toast toast-end toast-bottom z-50">
                <div class="alert alert-success bg-white text-black shadow-lg">
                    <IconCircleCheck class="w-5 h-5 text-green-500" />
                    <span>Draft saved successfully</span>
                </div>
            </div>

            <!-- Unsaved changes modal for Preview -->
            <dialog ref="unsavedChangesModal" class="modal">
                <div class="modal-box bg-white text-cs-gray-lv-5">
                    <div class="flex items-center gap-2">
                        <IconAlertTriangle class="w-8 h-8 text-yellow-500" />
                        <h3 class="font-bold text-lg">Unsaved Changes</h3>
                    </div>
                    <p class="py-4">You have unsaved changes. Would you like to save before previewing?</p>
                    <div class="modal-action">
                        <button class="button-cs-outline-primary-sm"
                            @click="$refs.unsavedChangesModal.close()">Cancel</button>
                        <button class="button-cs-primary-sm" @click="saveAndPreviewConfirmed">
                            Save & Preview
                        </button>
                    </div>
                </div>
                <form method="dialog" class="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </div>
    </div>
</template>

<script>
import Upload from './new_signed/upload.vue'
import Dropzone from './dropzone'
import Fields from './new_signed/fields.vue'
import MobileDrawField from './new_signed/mobile_draw_field.vue'
import Document from './new_signed/document.vue'
import Logo from './logo'
import Contenteditable from './contenteditable'
import DocumentPreview from './new_signed/preview.vue'
import DocumentControls from './controls'
import MobileFields from './new_signed/mobile_fields'
import { IconSend, IconDeviceFloppy, IconEye, IconInnerShadowTop, IconInfoCircle, IconCircleCheck, IconAlertTriangle } from '@tabler/icons-vue'
import { v4 } from 'uuid'
import { ref, computed } from 'vue'
import { en as i18nEn } from './i18n'

export default {
    name: 'TemplateBuilder',
    components: {
        Upload,
        Document,
        Fields,
        IconInfoCircle,
        IconCircleCheck,
        MobileDrawField,
        MobileFields,
        Logo,
        Dropzone,
        DocumentPreview,
        DocumentControls,
        IconInnerShadowTop,
        Contenteditable,
        IconSend,
        IconEye,
        IconDeviceFloppy,
        IconAlertTriangle
    },
    provide() {
        return {
            template: this.template,
            save: this.save,
            t: this.t,
            currencies: this.currencies,
            baseFetch: this.baseFetch,
            fieldTypes: this.fieldTypes,
            backgroundColor: this.backgroundColor,
            withPhone: this.withPhone,
            withPayment: this.withPayment,
            isPaymentConnected: this.isPaymentConnected,
            withFormula: this.withFormula,
            withConditions: this.withConditions,
            defaultDrawFieldType: this.defaultDrawFieldType,
            selectedAreaRef: computed(() => this.selectedAreaRef),
            fieldsDragFieldRef: computed(() => this.fieldsDragFieldRef)
        }
    },
    props: {
        template: {
            type: Object,
            required: true
        },
        i18n: {
            type: Object,
            required: false,
            default: () => ({})
        },
        backgroundColor: {
            type: String,
            required: false,
            default: ''
        },
        editable: {
            type: Boolean,
            required: false,
            default: true
        },
        withHelp: {
            type: Boolean,
            required: false,
            default: true
        },
        autosave: {
            type: Boolean,
            required: false,
            default: true
        },
        defaultFields: {
            type: Array,
            required: false,
            default: () => []
        },
        defaultRequiredFields: {
            type: Array,
            required: false,
            default: () => []
        },
        withSelectedFieldType: {
            type: Boolean,
            required: false,
            default: false
        },
        defaultDrawFieldType: {
            type: String,
            required: false,
            default: 'signature'
        },
        currencies: {
            type: Array,
            required: false,
            default: () => []
        },
        fieldTypes: {
            type: Array,
            required: false,
            default: () => []
        },
        defaultSubmitters: {
            type: Array,
            required: false,
            default: () => []
        },
        acceptFileTypes: {
            type: String,
            required: false,
            default: 'image/*, application/pdf'
        },
        baseUrl: {
            type: String,
            required: false,
            default: ''
        },
        withLogo: {
            type: Boolean,
            required: false,
            default: true
        },
        onUpload: {
            type: Function,
            required: false,
            default() {
                return () => { }
            }
        },
        onSave: {
            type: Function,
            required: false,
            default() {
                return () => { }
            }
        },
        onChange: {
            type: Function,
            required: false,
            default() {
                return () => { }
            }
        },
        withStickySubmitters: {
            type: Boolean,
            required: false,
            default: true
        },
        withUploadButton: {
            type: Boolean,
            required: false,
            default: true
        },
        withTitle: {
            type: Boolean,
            required: false,
            default: true
        },
        withFieldsList: {
            type: Boolean,
            required: false,
            default: true
        },
        authenticityToken: {
            type: String,
            required: false,
            default: ''
        },
        withDocumentsList: {
            type: Boolean,
            required: false,
            default: true
        },
        withPhone: {
            type: Boolean,
            required: false,
            default: false
        },
        withPayment: {
            type: Boolean,
            required: false,
            default: false
        },
        isPaymentConnected: {
            type: Boolean,
            required: false,
            default: false
        },
        withFormula: {
            type: Boolean,
            required: false,
            default: false
        },
        withConditions: {
            type: Boolean,
            required: false,
            default: false
        },
        onlyDefinedFields: {
            type: Boolean,
            required: false,
            default: false
        },
        fetchOptions: {
            type: Object,
            required: false,
            default: () => ({ headers: {} })
        }
    },
    data() {
        return {
            pendingFieldAttachmentUuids: [],
            documentRefs: [],
            drawField: null,
            drawOption: null,
            copiedArea: null,
            drawFieldType: null,
            dragField: null,
            dragFrom: null,
            isMobileFieldsOpen: false,
            isSaving: false,
            isAlertModalOpen: false,
            alertModalText: '',
            closedAccordions: {},
            isBreakpointLg: false,
            showDrawField: false,
            lastSavedSnapshot: '',
            showSaveToast: false
        }
    },
    computed: {
        selectedAreaRef: () => ref(),
        fieldsDragFieldRef: () => ref(),
        hasUnsavedChanges() {
            if (!this.lastSavedSnapshot) {
                return false
            }

            return this.lastSavedSnapshot !== this.currentSnapshot()
        },
        fieldAreasIndex() {
            const areas = {}

            this.template.fields.forEach((f) => {
                (f.areas || []).forEach((a) => {
                    areas[a.attachment_uuid] ||= {}

                    const acc = (areas[a.attachment_uuid][a.page] ||= [])

                    acc.push({ area: a, field: f })
                })
            })

            return areas
        },
        isAllRequiredFieldsAdded() {
            return !this.defaultRequiredFields?.some((f) => {
                return !this.template.fields?.some((field) => field.name === f.name)
            })
        },
        selectedField() {
            return this.template.fields.find((f) => f.areas?.includes(this.selectedAreaRef.value))
        },
        sortedDocuments() {
            return this.template.schema.map((item) => {
                return this.template.documents.find(doc => doc.uuid === item.attachment_uuid)
            })
        }
    },
    created() {
        if (!this.template.fields?.length && this.template.submitters?.length === 1) {
            if (this.template.submitters[0]?.name === 'First Party') {
                this.template.submitters[0].name = this.t('first_party')
            }
        }

        const existingSubmittersUuids = this.defaultSubmitters.map((name) => {
            return this.template.submitters.find(e => e.name === name)?.uuid
        })

        this.defaultSubmitters.forEach((name, index) => {
            const submitter = (this.template.submitters[index] ||= {})

            submitter.name = name

            if (existingSubmittersUuids.filter(Boolean).length) {
                submitter.uuid = existingSubmittersUuids[index] || submitter.uuid || v4()
            } else {
                submitter.uuid ||= v4()
            }
        })

        // Ensure all submitters have an email field initialized
        this.template.submitters.forEach(submitter => {
            if (!Object.prototype.hasOwnProperty.call(submitter, 'email')) {
                submitter.email = ''
            }
        })

        this.selectedSubmitter = this.template.submitters[0]
    },
    mounted() {
        this.undoStack = [JSON.stringify(this.template)]
        this.redoStack = []
        this.lastSavedSnapshot = this.currentSnapshot()

        this.$nextTick(() => {
            this.onWindowResize()
        })

        document.addEventListener('keyup', this.onKeyUp)
        window.addEventListener('keydown', this.onKeyDown)
        window.addEventListener('resize', this.onWindowResize)
        window.addEventListener('beforeunload', this.onBeforeUnload)

        this.$nextTick(() => {
            if (document.location.search?.includes('stripe_connect_success')) {
                document.querySelector('form[action="/auth/stripe_connect"]')?.closest('.dropdown')?.querySelector('label')?.focus()
            }
        })

        this.template.schema.forEach((item) => {
            if (item.pending_fields) {
                this.pendingFieldAttachmentUuids.push(item.attachment_uuid)
            }
        })
    },
    unmounted() {
        document.removeEventListener('keyup', this.onKeyUp)
        window.removeEventListener('keydown', this.onKeyDown)
        window.removeEventListener('resize', this.onWindowResize)
        window.removeEventListener('beforeunload', this.onBeforeUnload)
    },
    beforeUpdate() {
        this.documentRefs = []
    },
    methods: {
        t(key) {
            return this.i18n[key] || i18nEn[key] || key
        },
        currentSnapshot() {
            return JSON.stringify(this.template)
        },
        removePendingFields() {
            this.template.fields = this.template.fields.filter((f) => {
                return this.template.schema.find((item) => item.attachment_uuid === f.attachment_uuid && item.pending_fields)
            })

            this.save()
        },
        addField(type, area = null) {
            const field = {
                name: '',
                uuid: v4(),
                required: type !== 'checkbox',
                areas: area ? [area] : [],
                submitter_uuid: this.selectedSubmitter.uuid,
                type
            }

            if (['select', 'multiple', 'radio'].includes(type)) {
                field.options = [{ value: '', uuid: v4() }]
            }

            if (type === 'stamp') {
                field.readonly = true
            }

            if (type === 'date') {
                field.preferences = {
                    format: Intl.DateTimeFormat().resolvedOptions().locale.endsWith('-US') || new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).match(/\s(?:CST|CDT|PST|PDT|EST|EDT)$/) ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
                }
            }

            this.template.fields.push(field)

            this.save()
        },
        startFieldDraw({ name, type }) {
            const existingField = this.template.fields?.find((f) => f.submitter_uuid === this.selectedSubmitter.uuid && name && name === f.name)

            if (existingField) {
                this.drawField = existingField
            } else {
                const field = {
                    name: name || '',
                    uuid: v4(),
                    required: type !== 'checkbox',
                    areas: [],
                    submitter_uuid: this.selectedSubmitter.uuid,
                    type
                }

                if (['select', 'multiple', 'radio'].includes(type)) {
                    field.options = [{ value: '', uuid: v4() }]
                }

                if (type === 'stamp') {
                    field.readonly = true
                }

                if (type === 'date') {
                    field.preferences = {
                        format: Intl.DateTimeFormat().resolvedOptions().locale.endsWith('-US') || new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).match(/\s(?:CST|CDT|PST|PDT|EST|EDT)$/) ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
                    }
                }

                this.drawField = field
            }

            this.drawOption = null
        },
        undo() {
            if (this.undoStack.length > 1) {
                this.undoStack.pop()
                const stringData = this.undoStack[this.undoStack.length - 1]
                const currentStringData = JSON.stringify(this.template)

                if (stringData && stringData !== currentStringData) {
                    this.redoStack.push(currentStringData)

                    Object.assign(this.template, JSON.parse(stringData))

                    this.save()
                }
            }
        },
        redo() {
            const stringData = this.redoStack.pop()
            this.lastRedoData = stringData
            const currentStringData = JSON.stringify(this.template)

            if (stringData && stringData !== currentStringData) {
                if (this.undoStack[this.undoStack.length - 1] !== currentStringData) {
                    this.undoStack.push(currentStringData)
                }

                Object.assign(this.template, JSON.parse(stringData))

                this.save()
            }
        },
        onWindowResize(e) {
            const breakpointLg = 1024

            this.isBreakpointLg = this.$el.getRootNode().querySelector('div[data-v-app]').offsetWidth < breakpointLg
        },
        setDocumentRefs(el) {
            if (el) {
                this.documentRefs.push(el)
            }
        },
        scrollIntoDocument(item, pageIndex) {
            pageIndex = parseInt(pageIndex || 0)
            console.log('Builder: Navigating to document with page index:', pageIndex, 'typeof:', typeof pageIndex)
            const ref = this.documentRefs.find((e) => e.document.uuid === item.attachment_uuid)

            if (ref) {
                const documentEl = ref.$el
                documentEl.scrollIntoView({ behavior: 'smooth', block: 'start' })

                setTimeout(() => {
                    try {
                        // Use the data-page-number attribute to find the exact page
                        const pageElement = documentEl.querySelector(`[data-page-number="${pageIndex}"]`)

                        if (pageElement) {
                            console.log(`Found page element with data-page-number=${pageIndex}, scrolling to it`)
                            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            return
                        }

                        // Fallback: Try direct image access
                        const allImages = documentEl.querySelectorAll('img')
                        const pageImages = []

                        // Filter to find just the main document page images (not UI elements or thumbnails)
                        allImages.forEach(img => {
                            if (img.width > 100 && img.height > 100) { // Main document images are larger
                                pageImages.push(img)
                            }
                        })

                        if (pageImages && pageImages.length > 0 && pageIndex >= 0 && pageIndex < pageImages.length) {
                            console.log(`Found ${pageImages.length} page images, scrolling to page ${pageIndex}`)
                            // Scroll to the specific page image
                            pageImages[pageIndex].scrollIntoView({ behavior: 'smooth', block: 'start' })
                        } else {
                            console.log(`Fallback: could not find page images (${pageImages?.length || 0}) or invalid index (${pageIndex})`)
                            // Try alternative selectors as fallback
                            const pageContainers = documentEl.querySelectorAll('.relative.cursor-crosshair')
                            if (pageContainers && pageContainers.length > 0 && pageIndex >= 0 && pageIndex < pageContainers.length) {
                                console.log(`Found ${pageContainers.length} page containers, scrolling to page ${pageIndex}`)
                                pageContainers[pageIndex].scrollIntoView({ behavior: 'smooth', block: 'start' })
                            } else if (typeof ref.scrollToPage === 'function') {
                                // Last resort - try component method
                                ref.scrollToPage(pageIndex)
                            }
                        }
                    } catch (error) {
                        console.error('Error navigating to page:', error)
                    }
                }, 300)
            } else {
                console.warn('Could not find document with UUID:', item.attachment_uuid)
            }
        },
        getAreasForDocument(documentId, documentIndex) {
            console.log(`Getting areas for document ID ${documentId} at index ${documentIndex}`)

            // Find the document object
            const documentItem = this.template.schema[documentIndex]
            if (!documentItem) {
                console.log('Document item not found in schema')
                return {}
            }

            // Get the attachment UUID which uniquely identifies this document
            const attachmentUuid = documentItem.attachment_uuid
            if (!attachmentUuid) {
                console.log('No attachment UUID found for document')
                return {}
            }

            console.log(`Document ${documentIndex} has attachment UUID: ${attachmentUuid}`)

            // Create an empty result object to hold field data by page
            const result = {}

            // Count fields that match this document
            let fieldMatchCount = 0

            // Process each field that has areas attached to it
            this.template.fields.forEach(field => {
                if (!field.areas || !Array.isArray(field.areas) || field.areas.length === 0) return

                // Look at each area to see if it belongs to this document
                field.areas.forEach(area => {
                    if (!area || !area.attachment_uuid) return

                    // We only want areas for THIS document
                    if (area.attachment_uuid !== attachmentUuid) return

                    // We found a match!
                    fieldMatchCount++

                    // Make sure we have an array to store fields for this page
                    const pageNumber = area.page || 0
                    if (!result[pageNumber]) {
                        result[pageNumber] = []
                    }

                    // Add this field to the result
                    result[pageNumber].push({
                        area,
                        field
                    })
                })
            })

            console.log(`Found ${fieldMatchCount} matching fields for document ${documentIndex}`)
            console.log('Fields by page:', Object.keys(result))

            return result
        },
        clearDrawField() {
            this.drawField = null
            this.drawOption = null
            this.showDrawField = false

            if (!this.withSelectedFieldType) {
                this.drawFieldType = ''
            }
        },
        onKeyUp(e) {
            if (e.code === 'Escape') {
                this.clearDrawField()

                this.selectedAreaRef.value = null
            }

            if (this.editable && ['Backspace', 'Delete'].includes(e.key) && this.selectedAreaRef.value && document.activeElement === document.body) {
                this.removeArea(this.selectedAreaRef.value)

                this.selectedAreaRef.value = null
            }
        },
        onKeyDown(event) {
            if ((event.metaKey && event.shiftKey && event.key === 'z') || (event.ctrlKey && event.key === 'Z')) {
                event.stopImmediatePropagation()
                event.preventDefault()

                this.redo()
            } else if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
                event.stopImmediatePropagation()
                event.preventDefault()

                this.undo()
            } else if ((event.ctrlKey || event.metaKey) && event.key === 'c' && document.activeElement === document.body) {
                event.preventDefault()

                this.copiedArea = this.selectedAreaRef?.value
            } else if ((event.ctrlKey || event.metaKey) && event.key === 'v' && this.copiedArea && document.activeElement === document.body) {
                event.preventDefault()

                this.pasteField()
            } else if (this.selectedAreaRef.value && ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.key) && document.activeElement === document.body) {
                event.preventDefault()

                this.handleAreaArrows(event)
            }
        },
        handleAreaArrows(event) {
            if (!this.editable) {
                return
            }

            const area = this.selectedAreaRef.value
            const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
            const page = documentRef.pageRefs[area.page].$refs.image
            const rect = page.getBoundingClientRect()
            const diff = (event.shiftKey ? 5.0 : 1.0)

            if (event.key === 'ArrowRight' && event.altKey) {
                area.w = Math.min(Math.max(area.w + diff / rect.width, 0), 1 - area.x)
            } else if (event.key === 'ArrowLeft' && event.altKey) {
                area.w = Math.min(Math.max(area.w - diff / rect.width, 0), 1 - area.x)
            } else if (event.key === 'ArrowUp' && event.altKey) {
                area.h = Math.min(Math.max(area.h - diff / rect.height, 0), 1 - area.y)
            } else if (event.key === 'ArrowDown' && event.altKey) {
                area.h = Math.min(Math.max(area.h + diff / rect.height, 0), 1 - area.y)
            } else if (event.key === 'ArrowRight') {
                area.x = Math.min(Math.max(area.x + diff / rect.width, 0), 1 - area.w)
            } else if (event.key === 'ArrowLeft') {
                area.x = Math.min(Math.max(area.x - diff / rect.width, 0), 1 - area.w)
            } else if (event.key === 'ArrowUp') {
                area.y = Math.min(Math.max(area.y - diff / rect.height, 0), 1 - area.h)
            } else if (event.key === 'ArrowDown') {
                area.y = Math.min(Math.max(area.y + diff / rect.height, 0), 1 - area.h)
            }

            this.debouncedSave()
        },
        debouncedSave() {
            clearTimeout(this._saveTimeout)

            this._saveTimeout = setTimeout(() => {
                this.save()
            }, 700)
        },
        removeArea(area) {
            const field = this.template.fields.find((f) => f.areas?.includes(area))

            field.areas.splice(field.areas.indexOf(area), 1)

            if (!field.areas.length) {
                this.template.fields.splice(this.template.fields.indexOf(field), 1)
            }

            this.save()
        },
        pasteField() {
            const field = this.template.fields.find((f) => f.areas?.includes(this.copiedArea))
            const currentArea = this.selectedAreaRef?.value || this.copiedArea

            if (field && currentArea) {
                const area = {
                    ...JSON.parse(JSON.stringify(this.copiedArea)),
                    attachment_uuid: currentArea.attachment_uuid,
                    page: currentArea.page,
                    x: currentArea.x,
                    y: currentArea.y + currentArea.h * 1.3
                }

                if (['radio', 'multiple'].includes(field.type)) {
                    this.copiedArea.option_uuid ||= field.options[0].uuid
                    area.option_uuid = v4()

                    field.options.push({ uuid: area.option_uuid })

                    field.areas.push(area)
                } else {
                    this.template.fields.push({
                        ...JSON.parse(JSON.stringify(field)),
                        uuid: v4(),
                        areas: [area]
                    })
                }

                this.selectedAreaRef.value = area

                this.save()
            }
        },
        pushUndo() {
            const stringData = JSON.stringify(this.template)

            if (this.undoStack[this.undoStack.length - 1] !== stringData) {
                this.undoStack.push(stringData)

                if (this.lastRedoData !== stringData) {
                    this.redoStack = []
                }
            }
        },
        setDefaultAreaSize(area, type) {
            const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
            const pageMask = documentRef.pageRefs[area.page].$refs.mask

            if (type === 'checkbox') {
                area.w = pageMask.clientWidth / 30 / pageMask.clientWidth
                area.h = (pageMask.clientWidth / 30 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight)
            } else if (type === 'image') {
                area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
                area.h = (pageMask.clientWidth / 5 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight)
            } else if (type === 'signature' || type === 'stamp') {
                area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
                area.h = (pageMask.clientWidth / 5 / pageMask.clientWidth) * (pageMask.clientWidth / pageMask.clientHeight) / 2
            } else if (type === 'initials') {
                area.w = pageMask.clientWidth / 10 / pageMask.clientWidth
                area.h = (pageMask.clientWidth / 35 / pageMask.clientWidth)
            } else {
                area.w = pageMask.clientWidth / 5 / pageMask.clientWidth
                area.h = (pageMask.clientWidth / 35 / pageMask.clientWidth)
            }
        },
        onDraw(area) {
            if (this.drawField) {
                if (this.drawOption) {
                    const areaWithoutOption = this.drawField.areas?.find((a) => !a.option_uuid)

                    if (areaWithoutOption && !this.drawField.areas.find((a) => a.option_uuid === this.drawField.options[0].uuid)) {
                        areaWithoutOption.option_uuid = this.drawField.options[0].uuid
                    }

                    area.option_uuid = this.drawOption.uuid
                }

                if (area.w === 0 || area.h === 0) {
                    const previousArea = this.drawField.areas?.[this.drawField.areas.length - 1]

                    if (this.selectedField?.type === this.drawField.type) {
                        area.w = this.selectedAreaRef.value.w
                        area.h = this.selectedAreaRef.value.h
                    } else if (previousArea) {
                        area.w = previousArea.w
                        area.h = previousArea.h
                    } else {
                        this.setDefaultAreaSize(area, this.drawOption ? 'checkbox' : this.drawField?.type)
                    }

                    area.x -= area.w / 2
                    area.y -= area.h / 2
                }

                this.drawField.areas ||= []

                const insertBeforeAreaIndex = this.drawField.areas.findIndex((a) => {
                    return a.attachment_uuid === area.attachment_uuid && a.page > area.page
                })

                if (insertBeforeAreaIndex !== -1) {
                    this.drawField.areas.splice(insertBeforeAreaIndex, 0, area)
                } else {
                    this.drawField.areas.push(area)
                }

                if (this.template.fields.indexOf(this.drawField) === -1) {
                    this.template.fields.push(this.drawField)
                }

                this.drawField = null
                this.drawOption = null

                this.selectedAreaRef.value = area

                this.save()
            } else {
                const documentRef = this.documentRefs.find((e) => e.document.uuid === area.attachment_uuid)
                const pageMask = documentRef.pageRefs[area.page].$refs.mask

                let type = (pageMask.clientWidth * area.w) < 35 ? 'checkbox' : this.defaultDrawFieldType

                if (this.drawFieldType) {
                    type = this.drawFieldType
                } else if (this.defaultDrawFieldType) {
                    type = this.defaultDrawFieldType
                } else if (this.fieldTypes.length !== 0 && !this.fieldTypes.includes(type)) {
                    type = this.fieldTypes[0]
                }

                if (type === 'checkbox' && !this.drawFieldType && (this.template.fields[this.template.fields.length - 1]?.type === 'checkbox' || area.w)) {
                    const previousField = [...this.template.fields].reverse().find((f) => f.type === type)
                    const previousArea = previousField?.areas?.[previousField.areas.length - 1]

                    if (previousArea || area.w) {
                        const areaW = previousArea?.w || (30 / pageMask.clientWidth)
                        const areaH = previousArea?.h || (30 / pageMask.clientHeight)

                        if ((pageMask.clientWidth * area.w) < 5) {
                            area.x = area.x - (areaW / 2)
                            area.y = area.y - (areaH / 2)
                        }

                        area.w = areaW
                        area.h = areaH
                    }
                }

                if (this.drawFieldType && (area.w === 0 || area.h === 0)) {
                    if (this.selectedField?.type === this.drawFieldType) {
                        area.w = this.selectedAreaRef.value.w
                        area.h = this.selectedAreaRef.value.h
                    } else {
                        this.setDefaultAreaSize(area, this.drawFieldType)
                    }

                    area.x -= area.w / 2
                    area.y -= area.h / 2
                }

                if (area.w) {
                    this.addField(type, area)

                    this.selectedAreaRef.value = area
                }
            }
        },
        onDropfield(area) {
            const field = this.fieldsDragFieldRef.value || {
                name: '',
                uuid: v4(),
                submitter_uuid: this.selectedSubmitter.uuid,
                required: this.dragField.type !== 'checkbox',
                ...this.dragField
            }

            if (!this.fieldsDragFieldRef.value) {
                if (['select', 'multiple', 'radio'].includes(field.type)) {
                    field.options = [{ value: '', uuid: v4() }]
                }

                if (field.type === 'stamp') {
                    field.readonly = true
                }

                if (field.type === 'date') {
                    field.preferences = {
                        format: Intl.DateTimeFormat().resolvedOptions().locale.endsWith('-US') || new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(new Date()).match(/\s(?:CST|CDT|PST|PDT|EST|EDT)$/) ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
                    }
                }
            }

            const fieldArea = {
                x: (area.x - 6) / area.maskW,
                y: area.y / area.maskH,
                page: area.page,
                attachment_uuid: area.attachment_uuid
            }

            const previousField = [...this.template.fields].reverse().find((f) => f.type === field.type)

            let baseArea

            if (this.selectedField?.type === field.type) {
                baseArea = this.selectedAreaRef.value
            } else if (previousField?.areas?.length) {
                baseArea = previousField.areas[previousField.areas.length - 1]
            } else {
                if (['checkbox'].includes(field.type)) {
                    baseArea = {
                        w: area.maskW / 30 / area.maskW,
                        h: area.maskW / 30 / area.maskW * (area.maskW / area.maskH)
                    }
                } else if (field.type === 'image') {
                    baseArea = {
                        w: area.maskW / 5 / area.maskW,
                        h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH)
                    }
                } else if (field.type === 'signature' || field.type === 'stamp') {
                    baseArea = {
                        w: area.maskW / 5 / area.maskW,
                        h: (area.maskW / 5 / area.maskW) * (area.maskW / area.maskH) / 2
                    }
                } else if (field.type === 'initials') {
                    baseArea = {
                        w: area.maskW / 10 / area.maskW,
                        h: area.maskW / 35 / area.maskW
                    }
                } else {
                    baseArea = {
                        w: area.maskW / 5 / area.maskW,
                        h: area.maskW / 35 / area.maskW
                    }
                }
            }

            fieldArea.w = baseArea.w
            fieldArea.h = baseArea.h
            fieldArea.y = fieldArea.y - baseArea.h / 2

            if (field.type === 'cells') {
                fieldArea.cell_w = baseArea.cell_w || (baseArea.w / 5)
            }

            field.areas ||= []

            const lastArea = field.areas[field.areas.length - 1]

            if (lastArea) {
                fieldArea.x -= lastArea.w / 2
                fieldArea.w = lastArea.w
                fieldArea.h = lastArea.h
            }

            field.areas.push(fieldArea)

            this.selectedAreaRef.value = fieldArea

            if (this.template.fields.indexOf(field) === -1) {
                this.template.fields.push(field)
            }

            this.save()

            document.activeElement?.blur()
        },
        updateFromUpload(data) {
            this.template.schema.push(...data.schema)
            this.template.documents.push(...data.documents)

            if (data.fields) {
                this.template.fields = data.fields
            }

            if (data.submitters) {
                this.template.submitters = data.submitters

                if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
                    this.selectedSubmitter = this.template.submitters[0]
                }
            }

            this.$nextTick(() => {
                this.$refs.previews.scrollTop = this.$refs.previews.scrollHeight

                this.scrollIntoDocument(data.schema[0])
            })

            if (this.template.name === 'New Document') {
                this.template.name = this.template.schema[0].name
            }

            if (this.onUpload) {
                this.onUpload(this.template)
            }

            this.save()

            data.documents.forEach((attachment) => {
                if (attachment.metadata?.pdf?.fields?.length) {
                    this.pendingFieldAttachmentUuids.push(attachment.uuid)

                    attachment.metadata.pdf.fields.forEach((field) => {
                        field.submitter_uuid = this.selectedSubmitter.uuid

                        this.template.fields.push(field)
                    })
                }
            })
        },
        updateName(value) {
            this.template.name = value

            this.save()
        },
        onDocumentRemove(item) {
            if (window.confirm(this.t('are_you_sure'))) {
                this.template.schema.splice(this.template.schema.indexOf(item), 1)
            }

            const removedFieldUuids = []

            this.template.fields.forEach((field) => {
                [...(field.areas || [])].forEach((area) => {
                    if (area.attachment_uuid === item.attachment_uuid) {
                        field.areas.splice(field.areas.indexOf(area), 1)

                        removedFieldUuids.push(field.uuid)
                    }
                })
            })

            this.template.fields =
                this.template.fields.filter((f) => !removedFieldUuids.includes(f.uuid) || f.areas?.length)

            this.save()
        },
        onDocumentReplace(data) {
            const { replaceSchemaItem, schema, documents } = data

            this.template.schema.splice(this.template.schema.indexOf(replaceSchemaItem), 1, schema[0])
            this.template.documents.push(...documents)

            if (data.fields) {
                this.template.fields = data.fields

                const removedFieldUuids = []

                this.template.fields.forEach((field) => {
                    [...(field.areas || [])].forEach((area) => {
                        if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
                            field.areas.splice(field.areas.indexOf(area), 1)

                            removedFieldUuids.push(field.uuid)
                        }
                    })
                })

                this.template.fields =
                    this.template.fields.filter((f) => !removedFieldUuids.includes(f.uuid) || f.areas?.length)
            }

            if (data.submitters) {
                this.template.submitters = data.submitters

                if (!this.template.submitters.find((s) => s.uuid === this.selectedSubmitter?.uuid)) {
                    this.selectedSubmitter = this.template.submitters[0]
                }
            }

            this.template.fields.forEach((field) => {
                (field.areas || []).forEach((area) => {
                    if (area.attachment_uuid === replaceSchemaItem.attachment_uuid) {
                        area.attachment_uuid = schema[0].attachment_uuid
                    }
                })
            })

            if (this.onUpload) {
                this.onUpload(this.template)
            }

            this.save()
        },
        moveDocument(item, direction) {
            const currentIndex = this.template.schema.indexOf(item)

            this.template.schema.splice(currentIndex, 1)

            if (currentIndex + direction > this.template.schema.length) {
                this.template.schema.unshift(item)
            } else if (currentIndex + direction < 0) {
                this.template.schema.push(item)
            } else {
                this.template.schema.splice(currentIndex + direction, 0, item)
            }

            this.save()
        },
        maybeShowErrorTemplateAlert(e) {
            if (!this.isAllRequiredFieldsAdded) {
                e.preventDefault()

                const fields = this.defaultRequiredFields?.filter((f) => {
                    return !this.template.fields?.some((field) => field.name === f.name)
                })

                if (fields?.length) {
                    return alert(this.t('add_all_required_fields_to_continue') + ': ' + fields.map((f) => f.name).join(', '))
                }
            }

            if (!this.template.fields.length) {
                e.preventDefault()

                alert('Please draw fields to prepare the document.')
            } else {
                const submitterWithoutFields =
                    this.template.submitters.find((submitter) => !this.template.fields.some((f) => f.submitter_uuid === submitter.uuid))

                if (submitterWithoutFields) {
                    e.preventDefault()

                    alert(`Please add fields for the ${submitterWithoutFields.name}. Or, remove the ${submitterWithoutFields.name} if not needed.`)
                }
            }
        },
        onSaveDraftClick() {
            if (this.isSaving) {
                return
            }

            this.isSaving = true

            this.save({ force: true })
                .then(() => {
                    this.showSaveToast = true
                    setTimeout(() => { this.showSaveToast = false }, 2500)
                })
                .catch(() => {
                    alert('Unable to save draft. Please try again.')
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        onPreviewClick() {
            if (this.isSaving) {
                return
            }

            if (this.hasUnsavedChanges) {
                this.$refs.unsavedChangesModal.showModal()
                return
            }

            window.location.href = `/templates/${this.template.id}/form`
        },
        saveAndPreviewConfirmed() {
            this.$refs.unsavedChangesModal.close()
            this.isSaving = true
            const previewUrl = `/templates/${this.template.id}/form`

            this.save({ force: true })
                .then(() => {
                    window.location.href = previewUrl
                })
                .catch(() => {
                    alert('Unable to save draft. Please try again.')
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        onBeforeUnload(e) {
            if (this.hasUnsavedChanges) {
                e.preventDefault()
                e.returnValue = ''
            }
        },
        scrollToArea(area) {
            const documentRef = this.documentRefs.find((a) => a.document.uuid === area.attachment_uuid)

            if (documentRef) {
                documentRef.scrollToArea(area)
            }

            this.selectedAreaRef.value = area
        },
        toggleAccordion(index) {
            // Use Vue 3's reactivity system
            if (this.closedAccordions[index]) {
                // Remove the property using standard JavaScript
                const newAccordions = { ...this.closedAccordions }
                delete newAccordions[index]
                this.closedAccordions = newAccordions
            } else {
                // Set the property using spread operator
                this.closedAccordions = { ...this.closedAccordions, [index]: true }
            }
        },

        // Check if an accordion is closed
        isAccordionClosed(index) {
            return !!this.closedAccordions[index]
        },
        baseFetch(path, options = {}) {
            return fetch(this.baseUrl + path, {
                ...options,
                headers: {
                    'X-CSRF-Token': this.authenticityToken,
                    ...this.fetchOptions.headers,
                    ...options.headers
                }
            })
        },
        save({ force } = { force: false }) {
            this.pendingFieldAttachmentUuids = []

            if (this.onChange) {
                this.onChange(this.template)
            }

            this.$nextTick(() => {
                if (this.$el.closest('template-builder')) {
                    this.$el.closest('template-builder').dataset.template = JSON.stringify(this.template)
                }
            })

            this.pushUndo()

            if (!this.autosave && !force) {
                return Promise.resolve({})
            }

            const templatePayload = {
                name: this.template.name,
                schema: this.template.schema,
                submitters: this.template.submitters,
                fields: this.template.fields,
                preferences: { status: 'draft' }
            }

            return this.baseFetch(`/templates/${this.template.id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    template: templatePayload
                }),
                headers: { 'Content-Type': 'application/json' }
            }).then((response) => {
                if (!response.ok) {
                    throw new Error('Save failed')
                }

                this.lastSavedSnapshot = this.currentSnapshot()

                if (this.onSave) {
                    this.onSave(this.template)
                }

                return response
            })
        }
    }
}
</script>
